"use client";

import { useEffect } from "react";

function shouldHardNavigate(pathname: string): boolean {
  return (
    pathname.startsWith("/app") ||
    pathname === "/signout" ||
    pathname.startsWith("/signout/") ||
    pathname === "/login" ||
    pathname.startsWith("/login/")
  );
}

/**
 * Next.js App Router soft-nav can silently no-op on mobile (esp. field PWA):
 * Links take focus but never change the URL. Hard navigation still works.
 * Force assign for in-app + auth exit/entry paths.
 */
export function AppHardNavFallback() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const el = e.target;
      if (!(el instanceof Element)) return;
      const a = el.closest("a[href]");
      if (!(a instanceof HTMLAnchorElement)) return;
      if (a.target && a.target !== "_self") return;
      if (a.hasAttribute("download")) return;

      const raw = a.getAttribute("href");
      if (!raw || raw.startsWith("#") || raw.startsWith("mailto:") || raw.startsWith("tel:")) {
        return;
      }

      let url: URL;
      try {
        url = new URL(a.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (!shouldHardNavigate(url.pathname)) return;
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.assign(url.href);
    }

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
